import { useFlag } from '@calendly/feature-check-react'
import { usePrevious } from '@calendly/hooks'
import { I18n } from '@calendly/i18n'
import { AiNotetakerDuotoneIcon, AiNotetakerErrorDuotoneIcon } from '@calendly/ui/components/icons'
import { Loader } from '@calendly/ui/components/loader'
import { Tooltip } from '@calendly/ui/components/tooltip'
import { useState, useEffect, useRef, forwardRef } from 'react'
import { useLocation } from 'react-router-dom'

import { useGetUserQuery } from 'common'
import { StepOne, MI_MEETINGS_TOUR, handleCheckStepEnabled } from 'features/meeting_intelligence'
import { useMeetingsContext } from 'features/meetings/contexts/MeetingsContext'
import { useHasSpotlight, useGetNotetakerToggleState } from 'features/meetings/hooks'
import { TRACKING_EVENT_NAMES, useTrack } from 'hooks/analytics_events'
import {
  useAddTrackedExternalMeetingMutation,
  useAddTrackedMeetingMutation,
  useDestroyTrackedMeetingMutation,
  useRemoveTrackedMeetingBotMutation,
} from 'hooks/tracked_meetings/api/index.js'
import { isCalendlyMeeting } from 'shared/utils'


import { ToggleContainer, ToggleHandle, ToggleText, Wrapper, ToggleIcon } from './styles'
import { isEventInProgress, isEventInTheFuture } from '../../../../../shared/utils/get_badge_key'

// This method of getting analytics for the toggle is a little hacky. Ideally we would make the analytics calls
// in the toggle on and off callbacks, but we need to know the tracked meeting uuid for when the toggle is turned on.
// Unfortunately the mutation for creating a tracked meeting doesnt return the tracked meeting uuid. So the tracked
// meeting uuid is only available after the following process:
//  1. Toggle on mutation invalidates the state of the tracked meetings cache for this event
//  2. This invalidation triggers a refetch of the tracked meetings state for this event
//  3. The refetch triggers a rerender of this toggle component
// A better solution would be to return the new tracked meeting uuid from the toggle on mutation, but that requires
// more work and an additional PR in the monolith, and hence more time. This solution is fine for now but it would
// be nice to improve it in the future.
const useAnalyticsForToggle = (event, trackedMeetingUuid, notetakerAdded, trackFromContactPage = false) => {
  const track = useTrack()
  const { data: currentUser } = useGetUserQuery()

  // notetakerAdded will be undefined (when loading), true, or false
  const previousNotetakerAdded = usePrevious(notetakerAdded)
  const previousTrackedMeetingUuid = usePrevious(trackedMeetingUuid)

  useEffect(() => {
    if (previousNotetakerAdded !== notetakerAdded && previousNotetakerAdded !== undefined) {
      // At this point we know that notetakerAdded has either gone from false to true, or true to false, i.e. the
      // toggle has been clicked.
      // If the previous state of notetakerAdded was undefined, then it means the toggle finished loading, and we
      // don't want to track that as an interaction.
      const miEventUuid = notetakerAdded ? trackedMeetingUuid : previousTrackedMeetingUuid

      if (trackFromContactPage) {
        const action = notetakerAdded ? 'enable_notetaker' : 'disable_notetaker'

        track({
          name: TRACKING_EVENT_NAMES.AI_NOTETAKER_INTERACTION,
          properties: {
            action,
            section: 'interactions_feed_all',
            location: 'contact_details_page',
            is_calendly_meeting: isCalendlyMeeting(event),
            event_uuid: event?.uuid,
            mi_event_uuid: miEventUuid,
            user_uuid: currentUser?.uuid,
          }
        })
      } else {
        const action = notetakerAdded ? 'toggle_notetaker_on' : 'toggle_notetaker_off'
        track({
          name: TRACKING_EVENT_NAMES.AI_NOTETAKER_INTERACTION,
          properties: {
            action,
            location: 'meetings_page',
            event_uuid: event?.uuid,
            mi_event_uuid: miEventUuid,
            is_calendly_meeting: isCalendlyMeeting(event),
            user_uuid: currentUser?.uuid,
          }
        })
      }
    }
  },
  [
    notetakerAdded,
    previousNotetakerAdded,
    trackedMeetingUuid,
    previousTrackedMeetingUuid,
    currentUser?.uuid,
    event,
    track,
    trackFromContactPage
  ])
}

/**
 * NotetakerToggle Component
 *
 * Renders a toggle button for enabling/disabling meeting intelligence (AI notetaker) for a meeting.
 * The toggle is only displayed if the meeting is eligible and the user has permission to add it.
 *
 * Key features:
 * - Shows on/off state with visual feedback
 * - Handles loading states with a loader component
 * - Displays error state when recording isn't possible
 *
 * @param {Object} props - Component props
 * @param {Object} props.event - The meeting/event object
 * @param {Object} [props.toggleState] - Pre-computed toggle state from useGetNotetakerToggleState (avoids duplicate hook call)
 * @returns {React.ReactElement | null} The NotetakerToggle component or null if toggle shouldn't be shown
 */
export const NotetakerToggle = forwardRef(({ event, toggleState }, ref) => {
  const { trackFromContactPage } = useMeetingsContext() ?? { trackFromContactPage: false }
  const hasSpotlight = useHasSpotlight(event.key)
  // Use passed-in toggleState if available (from NotetakerControl) to avoid redundant computation
  // Hook still runs per React rules, but result is only used if toggleState not provided
  const computedToggleState = useGetNotetakerToggleState(event)
  const activeToggleState = toggleState || computedToggleState

  const { showNotetaker,
    notetakerAdded,
    trackedMeetingUuid,
    unsupportedLocation,
    showErrorState,
    isLoading,
    isRecapProcessingOrCompleted } = activeToggleState

  const [hasClickedToggle, setHasClickedToggle] = useState(false)
  const [clickToggleResult, setClickToggleResult] = useState(null)
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false)
  const lastClickTimeRef = useRef(0)
  const previousEventUuidRef = useRef(event?.uuid)
  const location = useLocation()
  const showStepOne = handleCheckStepEnabled(location, MI_MEETINGS_TOUR.STEP_ONE)

  // Optimistic UI: use clicked state if user has clicked, otherwise use actual state
  const isToggleOn = hasClickedToggle ? clickToggleResult : notetakerAdded

  const toggleDisabled = unsupportedLocation || isRecapProcessingOrCompleted

  const [addTrackedMeeting, addTrackedMeetingState] = useAddTrackedMeetingMutation()
  const [addExternalTrackedMeeting, addExternalTrackedMeetingState] = useAddTrackedExternalMeetingMutation()
  const [removeTrackedMeeting, removeTrackedMeetingState] = useDestroyTrackedMeetingMutation()
  const [removeTrackedMeetingBot, removeTrackedMeetingBotState] = useRemoveTrackedMeetingBotMutation()
  const trackedMeetingMutation = event.kind && event.kind !== 'CALENDLY'
    ? { state: addExternalTrackedMeetingState, fn: addExternalTrackedMeeting }
    : { state: addTrackedMeetingState, fn: addTrackedMeeting }

  useEffect(() => {
    if (!isLoading && !hasInitiallyLoaded) {
      setHasInitiallyLoaded(true)
    }
  }, [isLoading, hasInitiallyLoaded])

  // Reset optimistic state when event changes
  useEffect(() => {
    if (previousEventUuidRef.current !== event?.uuid && previousEventUuidRef.current !== undefined) {
      setHasClickedToggle(false)
      setClickToggleResult(null)
    }
    previousEventUuidRef.current = event?.uuid
  }, [event?.uuid])

  useEffect(() => {
    const hasError = trackedMeetingMutation.state.isError
      || removeTrackedMeetingBotState.isError
      || removeTrackedMeetingState.isError
    const isSuccess = hasClickedToggle && clickToggleResult === notetakerAdded

    if ((hasError || isSuccess) && hasClickedToggle) {
      setHasClickedToggle(false)
      setClickToggleResult(null)
    }
  }, [
    trackedMeetingMutation.state.isError,
    removeTrackedMeetingBotState.isError,
    removeTrackedMeetingState.isError,
    hasClickedToggle,
    clickToggleResult,
    notetakerAdded
  ])

  useAnalyticsForToggle(event, trackedMeetingUuid, notetakerAdded, trackFromContactPage)

  const isAnythingLoading = isLoading || trackedMeetingMutation.state.isLoading || removeTrackedMeetingState.isLoading
    || removeTrackedMeetingBotState.isLoading

  const hasMsTeamsFlag = useFlag('ms_teams_notetaker')
  let tooltipText = null
  if (unsupportedLocation && !isToggleOn) {
    tooltipText = hasMsTeamsFlag
      ? I18n.t('publisher.meeting_intelligence.notetaker_toggle.invalid_location_with_teams')
      : I18n.t('publisher.meeting_intelligence.notetaker_toggle.invalid_location')
  } else if (unsupportedLocation && isToggleOn) {
    tooltipText = I18n.t('publisher.meeting_intelligence.notetaker_toggle.notetaker_cant_record')
  } else if (isRecapProcessingOrCompleted && isEventInProgress(event)) {
    tooltipText = I18n.t('publisher.meeting_intelligence.notetaker_toggle.recap_processing')
  } else if (isToggleOn && isEventInProgress(event)) {
    tooltipText = I18n.t('publisher.meeting_intelligence.notetaker_toggle.remove_notetaker_and_generate_recap')
  } else if (isToggleOn) {
    tooltipText = I18n.t('publisher.meeting_intelligence.notetaker_toggle.remove_notetaker')
  } else if (!isToggleOn) {
    tooltipText = I18n.t('publisher.meeting_intelligence.notetaker_toggle.add_notetaker')
  }

  // Visibility conditions:
  // 1. Don't show toggle if it shouldn't be displayed and nothing is loading
  // 2. Show loader during initial data fetch
  if (!showNotetaker && hasInitiallyLoaded) {
    return null
  }
  if ((isAnythingLoading && !hasInitiallyLoaded)) {
    return <Loader size="small" />
  }

  const onToggleOn = () => {
    setHasClickedToggle(true)
    setClickToggleResult(true)
    trackedMeetingMutation.fn({
      eventId: event.uuid,
      eventUuid: event.uuid,
      event,
    })
  }

  const onToggleOff = () => {
    const uuidFromMutation = trackedMeetingMutation.state.data?.resource?.uuid
    const meetingUuid = uuidFromMutation || trackedMeetingUuid

    if (!meetingUuid) {
      return
    }

    setHasClickedToggle(true)
    setClickToggleResult(false)
    if (!isEventInTheFuture(event)) {
      removeTrackedMeetingBot({
        meetingUuid,
        foreignEventId: event.uuid,
      })
    } else {
      removeTrackedMeeting({
        meetingUuid,
        foreignEventId: event.uuid,
      })
    }
  }

  const handleToggle = () => {
    if (isAnythingLoading || unsupportedLocation || isRecapProcessingOrCompleted) {
      return
    }

    // Debounce: prevent spam clicking by requiring at least 1 second between clicks
    const now = Date.now()
    const timeSinceLastClick = now - lastClickTimeRef.current
    const DEBOUNCE_MS = 1000 // 1 second debounce

    if (timeSinceLastClick < DEBOUNCE_MS) {
      return
    }

    lastClickTimeRef.current = now

    if (isToggleOn) {
      onToggleOff()
    } else {
      onToggleOn()
    }
  }

  return (
    <Wrapper ref={ref}>
      <Tooltip text={tooltipText} placement="top">
        <ToggleContainer
          role="switch"
          aria-checked={isToggleOn}
          aria-label={
            isToggleOn
              ? I18n.t('publisher.meeting_intelligence.notetaker_toggle.on')
              : I18n.t('publisher.meeting_intelligence.notetaker_toggle.off')
          }
          aria-disabled={isAnythingLoading}
          togglePositionOn={isToggleOn}
          showErrorState={showErrorState}
          toggleDisabled={toggleDisabled}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            handleToggle()
          }}
        >
          <ToggleHandle
            aria-hidden="true"
            togglePositionOn={isToggleOn}
            toggleDisabled={toggleDisabled}
            isSpotlight={showStepOne && hasSpotlight}
          >
            <ToggleIcon
              togglePositionOn={isToggleOn}
            >
              {showErrorState ? <AiNotetakerErrorDuotoneIcon /> : <AiNotetakerDuotoneIcon />}
            </ToggleIcon>
          </ToggleHandle>
          <ToggleText
            togglePositionOn={isToggleOn}
            toggleDisabled={toggleDisabled}
          >
            {isToggleOn ? I18n.t('publisher.meeting_intelligence.notetaker_toggle.on')
              : I18n.t('publisher.meeting_intelligence.notetaker_toggle.off')}
          </ToggleText>
        </ToggleContainer>
      </Tooltip>
      <StepOne isVisible={hasSpotlight} />
    </Wrapper>
  )
})
